import React, { Fragment } from "react";

import { Col, Label, Row } from "reactstrap";
import i18n from "i18n";

import { SamplingPointSelect } from "pages/BasicSettings/SamplingSites/components/sampling-point-select";
import { SamplingSiteSelect } from "pages/BasicSettings/SamplingSites/components/sampling-site-select";
import { RichTextInput } from "components/rich-text-input.tsx/rich-text-input";
import SuperscriptInput from "components/superscript/superscript-input";
import { SelectWithOptions } from "./components/select-with-options";
import { InputSwitch } from "components/input-switch/input-switch";
import { LocationInput } from "components/location-input";
import { DurationFieldV2 } from "./components/durationV2";
import { DurationField } from "./components/duration";
import { GT } from "components";

interface IFormFields {
    fields: IFormField[];
    formData: any;
    setFormData?: any;
    errors?: IErrors;
    readonly?: boolean;
    onChange?: (e: any, f?: any) => void;
    onBeforeUpdate?: (data: any, name: string) => void;
}

export const FormFields = (props: IFormFields) => {
    const { fields, formData, errors, setFormData, readonly, onChange, onBeforeUpdate } = props;

    const handleChange = (field, e, f, extraData) => {
        let newData: any;
        let extraFieldsToSet: any = extraData;
        let { value, name } = e?.label ? { value: e.value, name: f.name } : e?.target || { value: e, name: f?.name || f };

        if (value === undefined) {
            value = null;
        }

        if (typeof extraData === "function") extraFieldsToSet = extraData(value, name);

        setFormData &&
            setFormData((prev) => {
                if (!name.includes(".")) {
                    switch (field.fieldType) {
                        case "checkbox":
                            newData = { ...prev, [name]: e?.checked, ...extraFieldsToSet };
                            break;
                        case "numeric":
                            newData = { ...prev, [name]: parseFloat(value), ...extraFieldsToSet };
                            break;
                        default:
                            newData = { ...prev, [name]: value, ...extraFieldsToSet };
                    }
                } else {
                    const [_name, unit] = name.split(".");
                    newData = {
                        ...prev,
                        [_name]: { ...prev?.[_name], [unit]: !value ? null : value },
                        ...extraFieldsToSet,
                    };
                }
                onBeforeUpdate && onBeforeUpdate(newData, name);
                return newData;
            });
    };

    return (
        <>
            {fields.map((field, index) =>
                field?.display || field?.display === undefined ? (
                    <FormFieldRenderer
                        key={index}
                        field={field}
                        formData={formData}
                        errors={errors}
                        index={index}
                        readonly={readonly}
                        onChange={(e, f) => (onChange || handleChange)(field, e, f, field?.extraData)}
                    />
                ) : undefined
            )}
        </>
    );
};

interface IFromFieldRenderer {
    field: IFormField;
    formData: any;
    index: number;
    errors?: IErrors;
    readonly?: boolean;
    onChange: (e: any, f?: any) => void;
}

const FormFieldRenderer = (props: IFromFieldRenderer) => {
    const { field, formData, errors, index, readonly = false, onChange } = props;
    const isAutofocus = (() => {
        if (field.autofocus) return true;
        if (typeof field.autofocus === "boolean" && !field.autofocus) return false;
        else return index == 0;
    })();

    return {
        select: (
            <SelectWithOptions
                key={index}
                formData={formData}
                errors={errors}
                field={field}
                onChange={onChange}
                readonly={readonly}
                autofocus={isAutofocus}
                {...field.props}
            />
        ),
        primeSelect: (
            <SelectWithOptions
                key={index}
                formData={formData}
                errors={errors}
                field={field}
                onChange={onChange}
                readonly={readonly}
                autofocus={isAutofocus}
                {...field.props}
            />
        ),
        multiSelect: (
            <SelectWithOptions
                key={index}
                formData={formData}
                errors={errors}
                field={field}
                onChange={onChange}
                readonly={readonly}
                autofocus={isAutofocus}
                {...field.props}
            />
        ),
        creatableSelect: (
            <SelectWithOptions
                key={index}
                formData={formData}
                errors={errors}
                field={field}
                onChange={onChange}
                readonly={readonly}
                autofocus={isAutofocus}
                {...field.props}
            />
        ),
        multiCreatableSelect: (
            <SelectWithOptions
                key={index}
                formData={formData}
                errors={errors}
                field={field}
                onChange={onChange}
                readonly={readonly}
                autofocus={isAutofocus}
                {...field.props}
            />
        ),
        multiCreatableSelectWithoutMenu: (
            <SelectWithOptions
                key={index}
                formData={formData}
                errors={errors}
                field={field}
                onChange={onChange}
                readonly={readonly}
                autofocus={isAutofocus}
                {...field.props}
            />
        ),
        input: (
            <GT.CustomInput
                key={index}
                labelText={field.label}
                name={field.name}
                value={field?.value || formData?.[field.name] || ""}
                handleChange={field.onChange || onChange}
                disabled={readonly || field.disabled}
                required={field.required}
                autofocus={isAutofocus}
                errors={field.errors || errors?.[field.name]}
                className={field.className}
                noLabel={!field.label}
                {...field.props}
            />
        ),
        numeric: (
            <GT.CustomInput
                key={index}
                type="number"
                labelText={field.label}
                name={field.name}
                options={field.options}
                value={field?.value || formData?.[field.name] || ""}
                handleChange={field.onChange || onChange}
                disabled={readonly || field.disabled}
                required={field.required}
                autofocus={isAutofocus}
                errors={field.errors || errors?.[field.name]}
                className={field.className}
                noLabel={!field.label}
                {...field.props}
            />
        ),
        superscript: (
            <SuperscriptInput
                key={index}
                errors={field.errors || errors?.[field.name]}
                required={field.required}
                disabled={readonly || field.disabled}
                className={field.className}
                handleChange={field.onChange || onChange}
                labelText={field.label}
                autofocus={isAutofocus}
                name={field.name}
                value={field?.value || formData?.[field.name] || ""}
                noLabel={!field.label}
                {...field.props}
            />
        ),
        checkbox: (
            <GT.CustomCheckbox
                key={index}
                labelText={field.label}
                name={field.name}
                options={field.options}
                checked={field?.value || formData?.[field.name]}
                handleChange={field.onChange || onChange}
                disabled={readonly || field.disabled}
                required={field.required}
                autofocus={isAutofocus}
                errors={field.errors || errors?.[field.name]}
                className={field.className}
                noLabel={!field.label}
                {...field.props}
            />
        ),
        textarea: (
            <GT.CustomInput
                key={index}
                type="textarea"
                labelText={field.label}
                name={field.name}
                options={field.options}
                value={field?.value || formData?.[field.name] || ""}
                handleChange={field.onChange || onChange}
                disabled={readonly || field.disabled}
                required={field.required}
                autofocus={isAutofocus}
                errors={field.errors || errors?.[field.name]}
                className={field.className}
                noLabel={!field.label}
                {...field.props}
            />
        ),
        richText: (
            <RichTextInput
                key={index}
                value={field?.value || formData?.[field.name]}
                name={field.name}
                onChange={(value) => (field.onChange || onChange)(value, { name: field.name })}
                errors={field.errors || errors?.[field.name]}
                label={field.label}
                readOnly={field.disabled}
                className="mb-20"
                {...field.props}
            />
        ),
        date: (
            <GT.CustomDatePicker
                key={index}
                labelText={field.label}
                name={field.name}
                selectedDate={field?.value || formData?.[field.name]}
                handleChange={field.onChange || onChange}
                disabled={readonly || field.disabled}
                required={field.required}
                autofocus={isAutofocus}
                errors={field.errors || errors?.[field.name]}
                className={field.className}
                noLabel={!field.label}
                {...field.props}
            />
        ),
        dateV2: (
            <GT.DateTimePicker
                key={index}
                label={field.label}
                name={field.name}
                value={field?.value || formData?.[field.name]}
                onChange={field.onChange || onChange}
                disabled={readonly || field.disabled}
                required={field.required}
                errors={field.errors || errors?.[field.name]}
                className={field.className}
                noLabel={!field.label}
                {...field.props}
            />
        ),
        dateTime: (
            <GT.DateTimePicker
                key={index}
                label={field.label}
                name={field.name}
                value={field?.value || formData?.[field.name]}
                onChange={field.onChange || onChange}
                disabled={readonly || field.disabled}
                required={field.required}
                errors={field.errors || errors?.[field.name]}
                className={field.className}
                noLabel={!field.label}
                showTime
                {...field.props}
            />
        ),
        duration: <DurationField field={field} formData={formData} onChange={onChange} errors={errors} key={index} />,
        durationV2: <DurationFieldV2 field={field} formData={formData} onChange={onChange} errors={errors} key={index} />,
        lookup: (
            <GT.LookupField
                key={index}
                errors={errors}
                handleChange={field.onChange || onChange}
                name={field.name}
                lookupName={field?.lookupName as string}
                label={field.label as string}
                value={field.value || formData?.[field.name]}
                selectProps={{ ...field.props }}
                disabled={readonly || (field.disabled as boolean)}
                required={field.required}
                useLookupDefaultValue={field.useLookupDefaultValue}
            />
        ),
        money: (
            <Row key={index}>
                <Col sm={9}>
                    <GT.CustomInput
                        labelText={field.label}
                        type="number"
                        name={`${field.name}.amount`}
                        handleChange={field.onChange || onChange}
                        value={field.value || formData?.[field.name]?.amount || ""}
                        errors={field.errors || errors?.[field.name]}
                        className={field.className}
                        disabled={readonly || field.disabled}
                        noLabel={!field.label}
                        colSm={8}
                        labelSm={4}
                        min={0}
                        {...field.props}
                    />
                </Col>
                <Col sm={3}>
                    <GT.PrimeSelect
                        placeholder="Currency"
                        name={`${field.name}.currency`}
                        value={field.value || formData?.[field.name]?.currency}
                        onChange={field.onChange || onChange}
                        options={field.options}
                        disabled={readonly || field.disabled}
                        errors={field.errors || errors?.[field.name]}
                        className={field.className}
                        showClear={false}
                        {...field.props}
                    />
                </Col>
            </Row>
        ),
        samplingPoint: (
            <SamplingPointSelect
                key={index}
                errors={field.errors || errors?.[field.name]}
                handleChange={field.onChange || onChange}
                value={field?.value || formData?.[field.name]}
                samplingSites={field.props?.samplingSites || []}
                disabled={readonly || field?.disabled}
                {...field.props}
            />
        ),
        samplingSite: (
            <SamplingSiteSelect
                key={index}
                value={field?.value || formData?.[field.name]}
                errors={field.errors || errors?.[field.name]}
                client={formData?.client}
                handleChange={field.onChange || onChange}
                url="crm/sampling-sites-formdata"
                {...field.props}
            />
        ),
        switch: (
            <InputSwitch
                key={index}
                label={field.label}
                name={field.name}
                options={field.options}
                checked={field?.value || formData?.[field.name]}
                onChange={field.onChange || onChange}
                disabled={readonly || field.disabled}
                required={field.required}
                autofocus={field.autofocus}
                errors={field.errors || errors?.[field.name]}
                className={field.className}
                noLabel={!field.label}
                {...field.props}
            />
        ),
        inputGroup: (
            <GT.PrimeInputGroup
                name={field.name}
                className={field.className}
                handleChange={field.onChange || onChange}
                labelText={field.label}
                required={field.required}
                key={index}
                value={field?.value || formData?.[field.name] || ""}
                errors={field.errors || errors?.[field.name]}
                {...field.props}
            />
        ),
        labLocation: (
            <LocationInput
                key={index}
                value={field?.value || formData?.[field.name]}
                name={field.name}
                onChange={field.onChange || onChange}
                errors={field.errors || errors?.[field.name]}
                disabled={readonly || field.disabled}
                label="Current location"
                required={field.required}
                {...field.props}
            />
        ),
        label: (
            <Label key={index} className={field.className}>
                {i18n.t(field.label || field.name)}
            </Label>
        ),
        custom: <Fragment key={index}>{field?.customElement}</Fragment>,
    }[field.fieldType];
};
